import dayjs from 'dayjs';
import {
    computeRunProgress,
    formatHorizonLabel,
    isIdeaLabRunActive,
    type IdeaLabRunDetail,
    type IdeaLabRunSummary,
    type IdeaLabVariantSummary
} from './ideaLabUtils';

/** Study page view model: the variant lineage tree, run tallies, and the runs list filter. */

export type StudyTab = 'results' | 'variants' | 'runs';

export const STUDY_TABS: readonly StudyTab[] = ['results', 'variants', 'runs'];

export const parseStudyTab = (value: string | null): StudyTab => STUDY_TABS.find((tab) => tab === value) ?? 'results';

export interface VariantTreeRow {
    variant: IdeaLabVariantSummary;
    /** Nesting under the nearest listed ancestor; 0 for roots. */
    depth: number;
}

/**
 * Variants in lineage order: each variant directly under its parent, siblings by creation (id).
 * A variant whose parent is not in the list (archived and hidden, or outside the study) is a root.
 */
export const buildVariantTree = (variants: readonly IdeaLabVariantSummary[]): VariantTreeRow[] => {
    const listedIds = new Set(variants.map((variant) => variant.id));
    const childrenByParent = new Map<number | null, IdeaLabVariantSummary[]>();

    for (const variant of variants) {
        const parentKey = variant.parentVariantId !== null && listedIds.has(variant.parentVariantId) ? variant.parentVariantId : null;
        const siblings = childrenByParent.get(parentKey);

        if (siblings) siblings.push(variant);
        else childrenByParent.set(parentKey, [variant]);
    }

    for (const siblings of childrenByParent.values()) siblings.sort((left, right) => left.id - right.id);

    const rows: VariantTreeRow[] = [];
    const visited = new Set<number>();
    const visit = (variant: IdeaLabVariantSummary, depth: number) => {
        if (visited.has(variant.id)) return;

        visited.add(variant.id);
        rows.push({ variant, depth });

        for (const child of childrenByParent.get(variant.id) ?? []) visit(child, depth + 1);
    };

    for (const root of childrenByParent.get(null) ?? []) visit(root, 0);

    // A parent cycle has no root; list any such variants rather than dropping them.
    for (const variant of [...variants].sort((left, right) => left.id - right.id)) visit(variant, 0);

    return rows;
};

/** Case-insensitive match on label, key, description, or "#id". */
export const variantMatchesQuery = (variant: IdeaLabVariantSummary, query: string): boolean => {
    const needle = query.trim().toLowerCase();

    if (needle === '') return true;

    return [variant.label, variant.variantKey, variant.description ?? '', `#${variant.id}`].some((field) =>
        field.toLowerCase().includes(needle)
    );
};

export interface StudyVariantStats {
    total: number;
    frozen: number;
    draft: number;
    archived: number;
    promoted: number;
    control: IdeaLabVariantSummary | null;
}

export const summarizeStudyVariants = (variants: readonly IdeaLabVariantSummary[]): StudyVariantStats => ({
    total: variants.length,
    frozen: variants.filter((variant) => variant.lifecycle === 'frozen').length,
    draft: variants.filter((variant) => variant.lifecycle === 'draft').length,
    archived: variants.filter((variant) => variant.lifecycle === 'archived').length,
    promoted: variants.filter((variant) => variant.promotionId !== null).length,
    control: variants.find((variant) => variant.isStudyControl && variant.lifecycle !== 'archived') ?? null
});

export interface StudyRunStats {
    total: number;
    completed: number;
    active: number;
    /** Failed or cancelled. */
    stopped: number;
    latest: IdeaLabRunSummary | null;
    /** Completed runs, newest first. */
    completedRuns: IdeaLabRunSummary[];
}

const newestFirst = (left: IdeaLabRunSummary, right: IdeaLabRunSummary): number => right.id - left.id;

export const summarizeStudyRuns = (runs: readonly IdeaLabRunSummary[]): StudyRunStats => {
    const completedRuns = runs.filter((run) => run.status === 'completed').sort(newestFirst);

    return {
        total: runs.length,
        completed: completedRuns.length,
        active: runs.filter(isIdeaLabRunActive).length,
        stopped: runs.filter((run) => run.status === 'failed' || run.status === 'cancelled').length,
        latest: [...runs].sort(newestFirst)[0] ?? null,
        completedRuns
    };
};

/**
 * Failed or cancelled run id → the first later completed run with identical frozen settings (an
 * exact retry). Runs fixed by changing settings have a different fingerprint and stay unmatched.
 */
export const buildRerunIndex = (runs: readonly IdeaLabRunSummary[]): Map<number, IdeaLabRunSummary> => {
    const completedByFingerprint = new Map<string, IdeaLabRunSummary[]>();

    for (const run of runs) {
        if (run.status !== 'completed') continue;

        const matches = completedByFingerprint.get(run.settingsFingerprint);

        if (matches) matches.push(run);
        else completedByFingerprint.set(run.settingsFingerprint, [run]);
    }

    for (const matches of completedByFingerprint.values()) matches.sort((left, right) => left.id - right.id);

    const index = new Map<number, IdeaLabRunSummary>();

    for (const run of runs) {
        if (run.status !== 'failed' && run.status !== 'cancelled') continue;

        const rerun = completedByFingerprint.get(run.settingsFingerprint)?.find((candidate) => candidate.id > run.id);

        if (rerun) index.set(run.id, rerun);
    }

    return index;
};

/**
 * The run the Results tab opens on: the newest completed run that compares at least two variants,
 * or the newest completed run when none does. `completedRuns` must be newest first.
 */
export const pickDefaultResultsRun = (completedRuns: readonly IdeaLabRunSummary[]): IdeaLabRunSummary | null =>
    completedRuns.find((run) => run.variantIds.length >= 2) ?? completedRuns[0] ?? null;

export const describeVariantCount = (count: number): string => `${count} ${count === 1 ? 'variant' : 'variants'}`;

export type RunListFilter = 'current' | 'all';

/** "current" hides failed and cancelled runs; completed and in-flight runs always show. */
export const filterStudyRuns = (runs: readonly IdeaLabRunSummary[], filter: RunListFilter): IdeaLabRunSummary[] =>
    [...runs].filter((run) => filter === 'all' || (run.status !== 'failed' && run.status !== 'cancelled')).sort(newestFirst);

/** Eastern wall-clock `YYYY-MM-DD HH:mm:ss` shown as a date, e.g. "Jul 20, 2021". */
export const formatWallClockDate = (value: string | null): string => (value === null ? '—' : dayjs(value).format('MMM D, YYYY'));

/** Eastern wall-clock `YYYY-MM-DD HH:mm:ss` shown as a short timestamp, e.g. "Sep 23, 2026 12:54". */
export const formatWallClockShort = (value: string | null): string => (value === null ? '—' : dayjs(value).format('MMM D, YYYY HH:mm'));

/** Compact run window, e.g. "Jul 20, 2021 → Sep 18, 2026". */
export const describeRunWindowShort = (run: Pick<IdeaLabRunSummary, 'startDateTime' | 'endDateTime'>): string =>
    `${formatWallClockDate(run.startDateTime)} → ${formatWallClockDate(run.endDateTime)}`;

/**
 * Work-unit status in words. Excluded units are accounted for, so a completed run with exclusions
 * reads as done rather than as "331/335".
 */
export const describeRunWorkUnits = (
    run: Pick<IdeaLabRunSummary, 'status' | 'workUnitCount' | 'completedWorkUnitCount' | 'failedWorkUnitCount' | 'excludedWorkUnitCount'>
): string => {
    const progress = computeRunProgress(run);
    const parts: string[] = [];

    if (isIdeaLabRunActive(run)) parts.push(`${progress.percent}% of ${progress.total} units`);
    if (progress.failed > 0) parts.push(`${progress.failed} failed`);
    if (progress.excluded > 0) parts.push(`${progress.excluded} excluded`);

    return parts.join(' · ');
};

/**
 * One-line summary of what a run froze, in reading order: bar size, universe, window, horizons,
 * lenses, and session.
 */
export const describeRunFacts = (
    run: Pick<
        IdeaLabRunDetail,
        | 'primaryDuration'
        | 'entities'
        | 'excludedWorkUnitCount'
        | 'startDateTime'
        | 'endDateTime'
        | 'walkForwardStartDateTime'
        | 'horizons'
        | 'includeUnderlying'
        | 'optionLens'
        | 'markets'
    >,
    optionStrategyName: string | null
): string[] => {
    const horizons = [...new Set(run.horizons.map((horizon) => horizon.horizonSeconds))].sort((left, right) => left - right);
    const lenses = [
        ...(run.includeUnderlying ? ['Underlying'] : []),
        ...(run.optionLens !== null
            ? [`Options (${optionStrategyName ?? `option strategy #${run.optionLens.optionBuyingStrategyId}`})`]
            : [])
    ];

    return [
        `${run.primaryDuration} bars`,
        `${run.entities.length} symbols${run.excludedWorkUnitCount > 0 ? ` (${run.excludedWorkUnitCount} excluded)` : ''}`,
        describeRunWindowShort(run),
        run.walkForwardStartDateTime === null
            ? 'no walk-forward window'
            : `walk-forward from ${formatWallClockDate(run.walkForwardStartDateTime)}`,
        `${horizons.length === 1 ? 'horizon' : 'horizons'} ${horizons.map(formatHorizonLabel).join(', ')}`,
        lenses.join(' + '),
        run.markets
            .map((market) => {
                const label = market.entityType === 'Asset' ? 'Stocks/ETFs' : market.entityType === 'AssetIndex' ? 'Indexes' : 'Crypto';

                return `${label}: ${market.afterHoursRemoved ? 'regular session only' : 'all hours'}`;
            })
            .join('; ')
    ];
};
